import { useQuery, type QueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { can } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { Field, field } from '../create/Field';
import { ghostButton } from '../ui/Button';
import { IconSettings } from '../icons';

/** Why a shop with no type on can pick none, where a NEW job is being made. */
const NEW_JOB_EMPTY =
  'Every new job needs a service type, and your shop has none in use.';

/**
 * The types a job may be GIVEN (a new job's, or an existing job's new one), from the shop's
 * list as `serviceType.list` answers: the ones that are on, in the order the server sent. The
 * picker offers exactly these, and each form's save rule checks the value it holds against
 * these (the new-job sheet's `newJobMissing`, and `ServiceTypeStep`'s press), so a type turned
 * off or deleted since it was picked counts as not picked.
 */
export function offeredServiceTypes<T extends { active: boolean }>(
  rows: readonly T[],
): T[] {
  return rows.filter((type) => type.active);
}

/**
 * What a control holding `value` has actually picked from `offered`: the value itself when it
 * is on offer, and '' (nothing) when it is not. The picker shows this, and every save rule asks
 * it (`newJobMissing`, `ServiceTypeStep`'s press), as does the Jobs list's filter
 * (`shownServiceTypeFilter`), so a control and its rule cannot disagree about a type that has
 * gone.
 */
export function pickedFrom(offered: readonly { id: string }[], value: string): string {
  return offered.some((type) => type.id === value) ? value : '';
}

/**
 * After a refused write that sent a picked type (a new job, a conversion, a set): fetch the
 * shop's list again. The usual refusal is a type turned off or deleted since the list was read;
 * once the list is fresh the picker stops offering it and the save rule counts it as missing
 * (`pickedFrom`), so the refused id cannot be sent again. Fire-and-forget, never awaited: a
 * paused refetch would hold the write pending.
 */
export function refetchServiceTypes(
  qc: QueryClient,
  trpc: ReturnType<typeof useTRPC>,
): void {
  void qc.invalidateQueries({ queryKey: trpc.serviceType.list.queryKey() });
}

/**
 * THE service type picker (ops insights Phase 1). The new-job sheet uses it, and so does every
 * service type step (`ServiceTypeStep`: scheduling a quote, ticket 04; approving a request, 05;
 * and giving existing jobs a type, 08, on the Jobs list and the job page), so all of them offer
 * the same types in the same order.
 *
 * It offers the types that are ON, in the order the server sends: sort order, the order the
 * shop set in Settings. A turned-off type stays on the jobs that have it and is offered for no
 * new work, and the server refuses it anyway ("…is turned off"); leaving it out here is the
 * difference between a choice that works and one that only ever says no.
 *
 * A shop with none on gets an empty state, not an empty dropdown: a required control with
 * nothing in it is a form that cannot be finished, with nothing on screen to say why. Why it
 * matters is the caller's to say (`emptyReason`: a new job needs one; an existing job cannot be
 * given one). Owner and office are sent to the Settings card (`/settings#service-types`);
 * anyone else is told who can fix it, because Settings is not theirs to open (LESSONS
 * 2026-08-10: a control on a page asks the same question the server does).
 *
 * A list it could not refresh is still offered, and says so (042): a type turned off since
 * it loaded would only be refused by the server, in words, so the stale list is still usable.
 * The notice sits outside the `<label>`, because its retry is a button.
 *
 * `value` is a service type id, or '' for none picked yet. A value this list no longer offers
 * (turned off or deleted since it was picked; a refused write refetches the list,
 * `refetchServiceTypes`) shows as the blank choice. The picker does not reset it: the form
 * that holds it treats it as missing (`newJobMissing`, `ServiceTypeStep`), so it cannot be
 * sent, and the next pick replaces it.
 */
export function ServiceTypePicker({
  value,
  onChange,
  autoFocus = false,
  label = 'Service type',
  emptyReason = NEW_JOB_EMPTY,
}: {
  value: string;
  onChange: (serviceTypeId: string) => void;
  /**
   * Focus the control as it appears: for a step a button press opens (`ServiceTypeStep`), so
   * focus follows the press into the question instead of staying behind, or falling to <body>
   * when the pressed button goes away.
   */
  autoFocus?: boolean;
  /**
   * The words above the control, in every state, so the form always says what it is asking.
   * "Set to" where another control on the page is also about service types (the Jobs list's
   * filter), so the two are told apart by name.
   */
  label?: string;
  /** The first sentence of the empty state: why having no type in use stops this form. */
  emptyReason?: string;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.serviceType.list.queryOptions());
  const canManage = can(useSession().role, 'serviceType', 'manage');
  const state = shownState(q, q.data);

  if (!q.data) {
    return (
      <Field as="div" label={label}>
        <UnresolvedState
          state={unresolved(state)}
          what="your service types"
          icon={IconSettings}
          error={q.error}
          onRetry={() => void q.refetch()}
          skeleton="h-10"
        />
      </Field>
    );
  }

  const offered = offeredServiceTypes(q.data);
  return (
    <div className="space-y-2">
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {offered.length === 0 ? (
        <Field as="div" label={label}>
          <EmptyState
            icon={IconSettings}
            title="No service types to pick from"
            description={
              canManage
                ? `${emptyReason} Add one, or turn one back on, in Settings.`
                : `${emptyReason} Ask an owner or the office to add one in Settings.`
            }
            action={
              canManage ? (
                <Link to="/settings" hash="service-types" className={ghostButton}>
                  Open service types in Settings
                </Link>
              ) : undefined
            }
          />
        </Field>
      ) : (
        <Field label={label}>
          <select
            className={field}
            value={pickedFrom(offered, value)}
            required
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Pick the kind of work…</option>
            {offered.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </Field>
      )}
    </div>
  );
}
